import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import { useT } from "@/lib/i18n";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const t = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", items: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.errors.missing);
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      trackEvent("contact_submitted", { has_phone: Boolean(form.phone) });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", items: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.errors.failed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-[#111111] border-t border-[#2A2A2A]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16">        <div className="lg:col-span-5">
          <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] font-semibold mb-5">
            {t.contact.eyebrow}
          </p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F5F5F0]">
            {t.contact.titleA}
            <br />
            <span className="italic text-[#A3A3A3]">{t.contact.titleB}</span>
          </h2>

          <div className="mt-10 space-y-6">
            <a
              href={BRAND.phoneHref}
              data-testid="contact-phone-link"
              onClick={() => trackEvent("call_click", { source: "contact" })}
              className="group flex items-center gap-4 py-4 border-t border-[#2A2A2A] hover:border-[#D4AF37] transition"
            >
              <Phone className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.6} />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{t.contact.labels.call}</div>
                <div className="font-serif-display text-2xl text-[#F5F5F0] group-hover:text-[#D4AF37] transition">
                  {BRAND.phone}
                </div>
              </div>
            </a>
            <a
              href={BRAND.emailHref}
              data-testid="contact-email-link"
              onClick={() => trackEvent("email_click", { source: "contact" })}
              className="group flex items-center gap-4 py-4 border-t border-[#2A2A2A] hover:border-[#D4AF37] transition"
            >
              <Mail className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.6} />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{t.contact.labels.email}</div>
                <div className="font-serif-display text-xl text-[#F5F5F0] group-hover:text-[#D4AF37] transition">
                  {BRAND.email}
                </div>
              </div>
            </a>
            <a
              href={BRAND.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-address-link"
              onClick={() => trackEvent("maps_click", { source: "contact" })}
              className="group flex items-center gap-4 py-4 border-t border-b border-[#2A2A2A] hover:border-[#D4AF37] transition"
            >
              <MapPin className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.6} />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{t.contact.labels.hours}</div>
                <div className="font-serif-display text-xl text-[#F5F5F0]">{BRAND.hours}</div>
                <div className="text-sm text-[#A3A3A3] mt-1 group-hover:text-[#D4AF37] transition">{BRAND.address}</div>
              </div>
            </a>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-[#0A0A0A] border border-[#2A2A2A] rounded-sm p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field
              label={t.contact.labels.name}
              testId="contact-input-name"
              value={form.name}
              onChange={onChange("name")}
              placeholder={t.contact.labels.nameP}
              required
            />
            <Field
              label={t.contact.labels.emailF}
              testId="contact-input-email"
              type="email"
              value={form.email}
              onChange={onChange("email")}
              placeholder={t.contact.labels.emailP}
              required
            />
            <Field
              label={t.contact.labels.phone}
              testId="contact-input-phone"
              type="tel"
              value={form.phone}
              onChange={onChange("phone")}
              placeholder={t.contact.labels.phoneP}
            />
            <Field
              label={t.contact.labels.items}
              testId="contact-input-items"
              value={form.items || ""}
              onChange={(e) => setForm({ ...form, items: e.target.value })}
              placeholder={t.contact.labels.itemsP}
            />
          </div>

          <div className="mt-6">
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">
              {t.contact.labels.message}
            </label>
            <textarea
              data-testid="contact-input-message"
              rows={5}
              value={form.message}
              onChange={onChange("message")}
              placeholder={t.contact.labels.messageP}
              required
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none rounded-sm px-4 py-3 text-[#F5F5F0] placeholder:text-[#555] transition"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-button"
            className="mt-8 inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#C49A26] disabled:opacity-60 text-[#0A0A0A] px-7 py-4 rounded-sm font-semibold tracking-wide transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-12px_rgba(212,175,55,0.6)]"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitting ? t.contact.sending : t.contact.submit}
          </button>
          <p className="mt-4 text-xs text-[#A3A3A3]">
            {t.contact.privacy}
          </p>
        </form>
      </div>

      {/* Embedded Google Map */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-16">
        <div className="relative w-full h-[360px] md:h-[420px] rounded-sm overflow-hidden border border-[#2A2A2A]">
          <iframe
            data-testid="contact-map-iframe"
            title="Digit-Studio · 1194 Budapest, Vas Gereben utca 211"
            src="https://www.google.com/maps?q=Digit-Studio%2C%201194%20Budapest%2C%20Vas%20Gereben%20utca%20211&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
}

function Field({ label, testId, type = "text", ...rest }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">{label}</label>
      <input
        type={type}
        data-testid={testId}
        {...rest}
        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none rounded-sm px-4 py-3 text-[#F5F5F0] placeholder:text-[#555] transition"
      />
    </div>
  );
}
